import { DocTypes, DocTypesKey, AutoPopulatePayload } from '@/components/admin/documents/upload/types.ts'

export const docNameRegex = '([A-Z]+)_((?:[1-4][QH])|FY)([0-9]{2})'

export const docTypes: DocTypes = {
  financials: 'financials',
  preliminaries: 'preliminaries',
  presentation: 'presentation',
  sustainability: 'sustainability',
  prospectus: 'prospectus',
  other: 'other',
}

export const isValidFinancialDoc = (type: DocTypesKey, name: string) => {
  return type === docTypes.financials && hasFinancialDocName(name)
}

export const isValidPrelimDoc = (type: DocTypesKey, name: string) => {
  return type === docTypes.preliminaries && hasPrelimDocName(name)
}

export const isValidPresentationDoc = (type: DocTypesKey, name: string) => {
  return type === docTypes.presentation && hasPresentationDocName(name)
}

export const isValidSustainabilityDoc = (type: DocTypesKey, name: string) => {
  return type === docTypes.sustainability && hasSustainabilityDocName(name)
}

export const isValidProspectusDoc = (type: DocTypesKey, name: string) => {
  return type === docTypes.prospectus && hasProspectusDocName(name)
}

export const isValidOtherDoc = (type: DocTypesKey, name: string) => {
  return (
    type === docTypes.other &&
    !(
      hasFinancialDocName(name) ||
      hasPrelimDocName(name) ||
      hasPresentationDocName(name) ||
      hasSustainabilityDocName(name) ||
      hasProspectusDocName(name)
    )
  )
}

export const hasFinancialDocName = (name: string) => {
  return name.match(`${docNameRegex}.pdf`)
}

export const hasPrelimDocName = (name: string) => {
  return name.match(`${docNameRegex}_prelim.pdf`)
}

export const hasPresentationDocName = (name: string) => {
  return name.match(`${docNameRegex}_P.pdf`)
}

export const hasSustainabilityDocName = (name: string) => {
  return name.match(`${docNameRegex}_S.pdf`)
}

export const hasProspectusDocName = (name: string) => {
  return name.match('([A-Z]+)_([0-9]{4})([0-9]{2}).pdf')
}

export const createAutopopulatePayload = async (
  item: { name: string; sourceUrl?: string },
  getCompanyId: (ticker: string) => Promise<string>,
) => {
  const payload: AutoPopulatePayload = {
    documentName: item.name,
    sourceUrl: item.sourceUrl,
  }

  const isFinancials = hasFinancialDocName(payload.documentName)
  const isPreliminary = hasPrelimDocName(payload.documentName)
  const isPresentation = hasPresentationDocName(payload.documentName)
  const isSustainability = hasSustainabilityDocName(payload.documentName)
  const isProspectus = hasProspectusDocName(payload.documentName)

  if (isPresentation) {
    payload.companyId = await getCompanyId(isPresentation[1])
    payload.documentType = 'presentation'
    payload.year = `20${isPresentation[3]}`
    payload.period = isPresentation[2]
  } else if (isFinancials) {
    payload.companyId = await getCompanyId(isFinancials[1])
    payload.documentType = 'financials'
    payload.year = `20${isFinancials[3]}`
    payload.period = isFinancials[2]
  } else if (isPreliminary) {
    payload.companyId = await getCompanyId(isPreliminary[1])
    payload.documentType = 'preliminaries'
    payload.year = `20${isPreliminary[3]}`
    payload.period = isPreliminary[2]
  } else if (isProspectus) {
    payload.companyId = await getCompanyId(isProspectus[1])
    payload.documentType = 'prospectus'
    payload.year = isProspectus[2]
    payload.month = isProspectus[3]
    payload.pageRange = '0'
  } else if (isSustainability) {
    payload.companyId = await getCompanyId(isSustainability[1])
    payload.documentType = 'sustainability'
    payload.year = `20${isSustainability[3]}`
    payload.period = isSustainability[2]
    payload.pageRange = '0'
  } else {
    const [ticker] = payload.documentName.split('_')
    payload.companyId = await getCompanyId(ticker)
    payload.documentType = 'other'
    payload.pageRange = '0'
  }
  return payload
}
